import { type RefObject, useRef } from "react";
import {
  mdiAspectRatio,
  mdiCheck,
  mdiClose,
  mdiCropFree,
  mdiLoading,
  mdiRestore,
  mdiRotateRight
} from "@mdi/js";
import Icon from "@mdi/react";
import { Cropper, type ReactCropperElement } from "react-cropper";

import { imageEditorControlClass } from "./controlStyles";
import { useCropperTouchGuard } from "./useCropperTouchGuard";
import { useImageFraming } from "./useImageFraming";
import styles from "./ImageFramingStage.module.scss";
import { ZoomRuler } from "./ZoomRuler";

interface ImageFramingStageProps {
  cropperRef: RefObject<ReactCropperElement>;
  file: { id: string; name: string; preview: string };
  isActive: boolean;
  isConfirming?: boolean;
  onCancel: () => void;
  onConfirm: () => Promise<void> | void;
}

export function ImageFramingStage({
  cropperRef,
  file,
  isActive,
  isConfirming = false,
  onCancel,
  onConfirm
}: Readonly<ImageFramingStageProps>) {
  const frameRef = useRef<HTMLDivElement>(null);
  const {
    aspectLabel,
    zoomValue,
    autoFrame,
    reset,
    rotate,
    cycleAspectRatio,
    changeZoom,
    syncZoom
  } = useImageFraming({ cropperRef, fileId: file.id, isActive });
  useCropperTouchGuard(frameRef, isActive);

  return (
    <div className="flex h-full min-h-0 flex-col overscroll-none bg-black text-white">
      <div className="relative z-10 flex flex-none items-center justify-between gap-2 px-4 pb-3 pt-4">
        <button type="button" onClick={autoFrame} className={imageEditorControlClass}>
          <Icon path={mdiCropFree} className="mr-2 size-5" />
          <span className="text-sm font-medium">Auto frame</span>
        </button>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={cycleAspectRatio}
            className={imageEditorControlClass}
            aria-label={`Aspect ratio ${aspectLabel}`}>
            <Icon path={mdiAspectRatio} className="mr-1 size-5" />
            <span className="text-xs">{aspectLabel}</span>
          </button>
          <button
            type="button"
            onClick={rotate}
            className={imageEditorControlClass}
            aria-label="Rotate photo">
            <Icon path={mdiRotateRight} className="size-5" />
          </button>
          <button
            type="button"
            onClick={reset}
            className={imageEditorControlClass}
            aria-label="Reset framing">
            <Icon path={mdiRestore} className="size-5" />
          </button>
        </div>
      </div>

      <div
        ref={frameRef}
        data-testid="cropper-touch-surface"
        className={`relative z-0 min-h-0 flex-1 touch-none select-none overflow-hidden bg-black ${styles.cropperFrame}`}>
        <Cropper
          key={file.id}
          src={file.preview}
          ref={cropperRef}
          style={{ height: "100%", width: "100%", touchAction: "none" }}
          viewMode={1}
          guides
          center
          responsive
          background={false}
          autoCrop
          autoCropArea={0.86}
          checkOrientation={false}
          dragMode="move"
          cropBoxMovable
          cropBoxResizable
          minCropBoxHeight={80}
          minCropBoxWidth={80}
          toggleDragModeOnDblclick={false}
          wheelZoomRatio={0.08}
          zoom={syncZoom}
          ready={autoFrame}
        />
      </div>

      <div className="relative z-10 flex-none px-4 pb-4 pt-5">
        <ZoomRuler value={zoomValue} onChange={changeZoom} />

        <div className="grid grid-cols-[3.5rem_1fr_3.5rem] items-center gap-4 px-1">
          <button
            type="button"
            onClick={onCancel}
            className="flex size-14 items-center justify-center rounded-full bg-white/15 transition active:scale-95 active:bg-white/25"
            aria-label="Cancel framing">
            <Icon path={mdiClose} className="size-7" />
          </button>
          <span className="text-center text-lg font-medium">
            {isConfirming ? "Processing photo…" : "Framing"}
          </span>
          <button
            type="button"
            onClick={() => void onConfirm()}
            disabled={isConfirming}
            className="flex size-14 items-center justify-center rounded-full bg-white text-black shadow-lg shadow-black/30 transition active:scale-95 active:bg-white/90 disabled:cursor-not-allowed disabled:opacity-70 disabled:active:scale-100"
            aria-label={isConfirming ? "Processing photo" : "Confirm framing"}>
            <Icon
              path={isConfirming ? mdiLoading : mdiCheck}
              spin={isConfirming ? 1 : undefined}
              className="size-7"
            />
          </button>
        </div>
      </div>
    </div>
  );
}
